import type { Rule as ValidationRule } from 'ant-design-vue/es/form/interface';
import { dateUtil, isArray, isNil } from '@mts-fe/utils';
import { t } from '@mts/hooks/web/useI18n';
import type { FormSchema } from './types/form';
import type { ComponentType } from './types';

const INPUT_COMPONENTS = ['Input', 'Complete', 'Textarea', 'Stepper'];
const SELECT_COMPONENTS = [
  'ApiSelect',
  'ApiSelectAdd',
  'Select',
  'Cascader',
  'ApiSegmented',
  'Checkbox',
  'Radio',
  'Switch',
  'Picker',
];

/**
 * @description: 生成placeholder
 */
export function createPlaceholderMessage(component: ComponentType) {
  if (INPUT_COMPONENTS.some((i) => component.includes(i))) return t('common.inputText');

  if (SELECT_COMPONENTS.some((i) => component.includes(i))) return t('common.chooseText');
}

const DATE_TYPE: ComponentType[] = ['DatePicker', 'MonthPicker', 'WeekPicker', 'TimePicker'];
export const RANGE_TYPE: ComponentType[] = ['RangePicker', 'QuickRangePicker'];
export const dateItemType: ComponentType[] = [...DATE_TYPE, ...RANGE_TYPE, 'TimeRangePicker'];

/**
 * 上传组件
 */
const uploadItemType: ComponentType[] = ['Upload', 'ImageUpload'];
export const itemIsUploadComponent = (key: ComponentType) => uploadItemType.includes(key);

const NUMBER_COMPONENTS = ['InputNumber', 'Stepper'];
export function setComponentRuleType(
  rule: ValidationRule,
  component: ComponentType,
  valueFormat: string,
) {
  if (Reflect.has(rule, 'type')) {
    return;
  }
  if (DATE_TYPE.includes(component)) {
    rule.type = valueFormat ? 'string' : 'object';
  } else if ([...RANGE_TYPE, 'CheckboxGroup'].includes(component)) {
    rule.type = 'array';
  } else if (NUMBER_COMPONENTS.includes(component)) {
    rule.type = 'number';
  }
}

export const defaultValueComponents = [
  'Input',
  'InputPassword',
  'InputNumber',
  'InputSearch',
  'InputTextArea',
];

export const getSimpleDefaultValue = (schemas?: FormSchema[]): Recordable => {
  if (!schemas || schemas?.length === 0) return {};

  const value: Recordable = {};
  for (const schema of schemas) {
    if (!isNil(schema.defaultValue)) {
      let defaultValue = schema.defaultValue;

      const component = schema['component'] as ComponentType;
      if (component && dateItemType.includes(component)) {
        defaultValue = formatDateDefaultValue(defaultValue, schema.componentProps?.valueFormat);
      }
      value[schema.field] = defaultValue;
    }
  }
  return value;
};

const formatDateDefaultValue = (defaultValue, valueFormat?: string) => {
  if (!valueFormat) return defaultValue;

  if (isArray(defaultValue)) {
    const def: any[] = [];
    for (let i = 0; i < defaultValue.length; i++) {
      const item = defaultValue[i];
      def.push(valueFormat ? dateUtil(item).format(valueFormat) : dateUtil(item));
    }
    return def;
  }

  return dateUtil(defaultValue).format(valueFormat);
};

// TODO 自定义组件封装会出现验证问题，因此这里目前改成手动触发验证
export const NO_AUTO_LINK_COMPONENTS: ComponentType[] = [
  'Upload',
  'MUpload',
  'ApiTransfer',
  'ApiTree',
  'ApiTreeSelect',
  'ApiButtonGroup',
  'ApiRadioGroup',
  'ApiCascader',
  'AutoComplete',
  'RadioButtonGroup',
  'ImageUpload',
  'ApiSelect',
  'ApiSelectAdd',
  'ModalTableSelect',
  'QuickRangePicker',
  'IconPicker',
  'InputCode',
  'InputRange',
  'LongText',
  'ReadonlyText',
  'Stepper',
  'VirtualInput',
];

export const simpleComponents = ['Divider', 'BasicTitle'];

export function isIncludeSimpleComponents(component?: ComponentType) {
  return simpleComponents.includes(component || '');
}

const NOT_TEXT_COMPS = ['Switch', 'MUpload', 'InputRange'];
export const getReadonlyComponent = (component?: ComponentType, longText?: boolean) => {
  if (!component) return component;

  return NOT_TEXT_COMPS.includes(component) ? component : longText ? 'LongText' : 'ReadonlyText';
};

const IGNORE_ACTION_PROPS = [
  'showActionButtonGroup',
  'showResetButton',
  'showSubmitButton',
  'showAdvancedButton',
  'showMoreButton',
  'resetButtonOptions',
  'submitButtonOptions',
  'moreButtonOptions',
  'moreModalProps',
  'actionColOptions',
  'actionSpan',
  'isAdvanced',
  'hideAdvanceBtn',
  'readonlyMode',
] as const;
export const IGNORE_PROPS_KEYS = [
  '_ID',
  'baseColProps',
  'rowProps',
  'tableAction',
  'labelWidth',
  'labelEllipsis',
  'fieldMapToTime',
  'inlineTextSplit',
  'hideLabel',
  'flatParam',
  'compact',
  'inset',
  'schemas',
  'autoSetPlaceHolder',
  'autoSubmitOnEnter',
  'enterFocus',
  'scanFocusNext',
  'submitOnReset',
  'emptySpan',
  'transformDateFunc',
  'rulesMessageJoinLabel',
  'autoAdvancedLine',
  'alwaysShowLines',
  'strongRelate',
  'checkSelect',
  'updateRecord',
  'columnSpan',
  ...IGNORE_ACTION_PROPS,
] as const;
